import { useState } from "react";
import * as React from "react";
import { Pencil, X } from "lucide-react";
import {
  useRulesData,
  useDeleteRule,
  useCreateRule,
  useUpdateRule,
  useApproveRule,
  useCreateComment,
  useGetFilterRule,
} from "@/hooks/useLocalStorage";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronRight,
  Plus,
  Edit,
  Trash2,
  TreePine,
  List,
  Loader2,
  Check,
  Wrench,
} from "lucide-react";
import {
  Rule,
  Code,
  insertRuleSchema,
  InsertRule,
} from "../../shared/schema";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Textarea } from "../ui/textarea";
import { useLocation } from "wouter";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { FilterTypeSearch } from "@/widgets/searchFilterType";

interface HierarchicalNodeProps {
  item: Rule;
  level?: number;
}

interface TableHierarchicalNodeProps {
  topLevelTypesData: Rule[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (code: number) => void;
}

interface PopupProps {
  popupName: string;
  heading: Rule | null;
  content: string | null | undefined;
  onClose: () => void;
  onChange?: (comm: string, data: Rule) => void;
  isPending?: boolean;
}

type FilterType = {
  ID?: number | undefined;
  Name: string;
  DataTypeName: string | null;
  ApplicationID: number;
  Status: number;
  Approved: number;
};


function CreateRulesDialog({
  trigger,
  editData,
  isEdit = false,
}: {
  trigger: React.ReactNode;
  editData?: Rule;
  isEdit?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [reqStatus, setReqStatus] = React.useState(false);
  const [currentData, setCurrentData] = useState<Rule[]>([]);

  const { data: existingDtValues, isLoading } = useRulesData(
    1,
    undefined,
    undefined
  );

  const createRuleMutation = useCreateRule();
  const updateRuleMutation = useUpdateRule();

  const objectTypeOptions = [
    { value: "ContentReviewApproved", label: "ContentReviewApproved" },
    { value: "ContentReviewRejected", label: "ContentReviewRejected" },
    { value: "Credit Rating Rules", label: "PublishContent(WebPortal)" },
  ];

  React.useEffect(() => {
    if (existingDtValues && existingDtValues.total > 0) {
      setCurrentData(existingDtValues.items);
    }
  }, [existingDtValues]);

  const form = useForm<InsertRule>({
    resolver: zodResolver(insertRuleSchema),
    defaultValues: {
      Name: "",
      Body: "",
      Description: "",
      DataTypeName: "",
      ObjectTypeFor: "",
      ApplicationID: 0,
      Type: 0,
      Status: 0,
      Approved: 0,
      Rule: "H",
      RuleExpression: "",
      EvaluationStartMessage: "",
      EvaluationSuccessMessage: "",
      EvaluationFailMessage: "",
      PublishFlag: false,
      RuleExpressionJson: "",
      ExistingRuleID: undefined,
      ID: 0,
    },
  });

  React.useEffect(() => {
    if (isEdit && editData) {
      form.reset({
        Name: editData.Name || "",
        Body: editData.Body || "",
        Description: editData.Description || "",
        DataTypeName: editData.DataTypeName || "",
        ObjectTypeFor: editData.ObjectTypeFor || "",
        ApplicationID: editData.ApplicationID || 0,
        Type: editData.Type || 0,
        Status: editData.Status || 0,
        Approved: editData.Approved || 0,
        Rule: editData.Rule || "",
        RuleExpression: editData.RuleExpression || "",
        EvaluationStartMessage: editData.EvaluationStartMessage || "",
        EvaluationSuccessMessage: editData.EvaluationSuccessMessage || "",
        EvaluationFailMessage: editData.EvaluationFailMessage || "",
        PublishFlag: editData.PublishFlag || false,
        RuleExpressionJson: editData.RuleExpressionJson || "",
        ExistingRuleID: 0,
        ID: editData.ID,
      });
    }
  }, [isEdit, editData, form]);

  const handleRuleCopySelect = (selectedId: number | undefined) => {
    const selectedRule = currentData.find(r => r.ID === selectedId);
    if (!selectedRule) return;

    form.setValue("Name", selectedRule.Name || "");
    form.setValue("ExistingRuleID", selectedRule.ID);
    form.setValue("ObjectTypeFor", selectedRule.ObjectTypeFor || "");
    form.setValue("Description", selectedRule.Description || "");
    form.setValue("EvaluationStartMessage", selectedRule.EvaluationStartMessage || "");
    form.setValue("EvaluationSuccessMessage", selectedRule.EvaluationSuccessMessage || "");
    form.setValue("EvaluationFailMessage", selectedRule.EvaluationFailMessage || "");
  };

  const onSubmit = async (data: InsertRule) => {
    try {
      if (isEdit && editData) {
        const updatePayload = {
          id: editData.ID,
          updates: {
            ...data,
            ID: editData.ID,
          },
        };
        updateRuleMutation.mutateAsync(updatePayload);
      } else {
        createRuleMutation.mutateAsync(data);
      }
    } catch (error) {
      console.error("Error saving rule:", error);
      setAlertMessage("Failed to save rule");
      setReqStatus(false);
      setAlertOpen(true);
    }
  };

  React.useEffect(() => {
    if (createRuleMutation.isSuccess === true) {
      setOpen(false);
      setReqStatus(createRuleMutation.isSuccess);
      setAlertMessage(`Rule Added Successfully!`);
      setAlertOpen(true);
      createRuleMutation.reset();
    } else if (createRuleMutation.isError) {
      const raw = createRuleMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(createRuleMutation.isSuccess);
      setOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      createRuleMutation.reset();
    }
  }, [createRuleMutation]);

  React.useEffect(() => {
    if (updateRuleMutation.isSuccess === true) {
      setOpen(false);
      setReqStatus(updateRuleMutation.isSuccess);
      setAlertMessage(`Rule Updated Successfully!`);
      setAlertOpen(true);
      updateRuleMutation.reset();
    } else if (updateRuleMutation.isError) {
      const raw = updateRuleMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(updateRuleMutation.isSuccess);
      setOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      updateRuleMutation.reset();
    }
  }, [updateRuleMutation]);
  console.log("currentData", editData)
  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <div className="flex flex-row justify-between">
                <div>Create Rules</div>
                {editData?.Approved === 1 &&
                  <div>
                    <p className="mr-10 text-sm text-red-600">UnApprove the Rule, for Edit Access</p>
                  </div>
                }
              </div>
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, (errors) =>
                console.log("errors", errors)
              )}
              className="space-y-4"
            >
              <div className="flex gap-4">
                <div className="flex-1">
                  <FormField
                    control={form.control}
                    name="ExistingRuleID"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Copy From Rules Repository To Modify
                        </FormLabel>
                        <FormControl>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                className="flex w-full justify-between items-center"
                                disabled={isEdit}
                              >
                                {field.value
                                  ? currentData.find((opt) => opt.ID === field.value)?.Name
                                  : "Select Rule"}
                                <ChevronRight className="ml-2 h-4 w-4 opacity-50" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="max-h-40 overflow-y-auto w-[280px]">
                              {currentData.map((opt) => (
                                <DropdownMenuItem
                                  className="w-[280px]"
                                  key={opt.Name}
                                  onSelect={() => handleRuleCopySelect(opt.ID)}
                                >
                                  {opt.ID}{" "}{opt.Name}
                                  {field.value === opt.ID && (
                                    <Check className="ml-auto h-4 w-4" />
                                  )}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex-1">
                  <FormField
                    control={form.control}
                    name="Name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter Rule Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex gap-4">

                <div className="flex-1">
                  <FormField
                    control={form.control}
                    disabled
                    name="ID"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rule ID</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter Rule ID" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex-1">
                  {/* <FormField
                    control={form.control}
                    name="EvaluationStartMessage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Evaluation Start Message</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter Evaluation Start"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  /> */}
                </div>

              </div>

              {/* <div className="flex gap-4">

                <div className="flex-1">
                  <FormField
                    control={form.control}
                    name="EvaluationSuccessMessage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Evaluation Success Message</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter Evaluation Success"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex-1">
                  <FormField
                    control={form.control}
                    name="EvaluationFailMessage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Evaluation Fail Message</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter Evaluation Fail"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

              </div> */}

              {/* <div className="flex gap-4">
                <div className="flex-1">
                  <FormField
                    control={form.control}
                    name="Description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter Description"
                            {...field}
                            className="resize-y min-h-[60px] max-h-[200px]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div> */}

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={createRuleMutation.isPending || editData?.Approved === 1 || updateRuleMutation.isPending}
                  onClick={() => form.reset({
                    Name: "",
                    Description: "",
                    ObjectTypeFor: "",
                    EvaluationStartMessage: "",
                    EvaluationSuccessMessage: "",
                    EvaluationFailMessage: "",
                  })}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  className="bg-workfinity-500 hover:bg-workfinity-600"
                  disabled={
                    createRuleMutation.isPending || updateRuleMutation.isPending || editData?.Approved === 1
                  }
                >
                  {createRuleMutation.isPending ||
                    updateRuleMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isEdit ? "Updating Rule..." : "Creating Rule..."}
                    </>
                  ) : isEdit ? (
                    "Update Rule"
                  ) : (
                    "Create Rule"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        message={alertMessage}
        status={reqStatus}
      />
    </div >
  );
}

function Popup({ popupName, heading, content, onClose, onChange, isPending }: PopupProps) {
  const [commentText, setCommentText] = useState("");

  React.useEffect(() => {
    const isCode =
      typeof content === "string" &&
      (content.includes("\\n") || content.includes("{") || content.includes("}"));

    const formattedContent = isCode ? content.replace(/\\r?\\n/g, "\n") : content;
    if (formattedContent) {
      setCommentText(formattedContent);
    }
  }, [content]);


  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rule {popupName}</DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4 text-sm text-gray-800">
          <div>
            <label className="font-semibold text-black">Rule Name :</label>
            <div className="border border-gray-400 rounded-md p-2 mt-1 bg-white">
              {heading?.Name || ""}
            </div>
          </div>

          <div>
            <label className="font-semibold text-black">
              {popupName}
            </label>
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className="min-h-[200px] resize-y flex-1 border border-gray-300 rounded-md p-3"
              style={{ outline: "none", boxShadow: "none" }}
              onFocus={(e) => {
                e.target.style.outline = "none";
                e.target.style.boxShadow = "none";
                e.target.style.borderColor = "#d1d5db";
              }}
              disabled={isPending || popupName !== "Comment"}
            />
          </div>

          {popupName === "Comment" &&
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCommentText("")}
                disabled={isPending}
              >
                Clear
              </Button>
              <Button
                type="submit"
                className="bg-workfinity-500 hover:bg-workfinity-600"
                onClick={() => { if (heading) onChange?.(commentText, heading); }}
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    "Saving..."
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          }
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TableRule({
  topLevelTypesData,
  pageIndex,
  pageSize,
  totalCount,
  onPageChange,
}: TableHierarchicalNodeProps) {
  const [currentData, setCurrentData] = useState<Rule[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Rule | null>(null);
  const [itemToApprove, setItemToApprove] = useState<Rule | null>(null);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [reqStatus, setReqStatus] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState(false);
  const [dataHead, setDataHead] = React.useState<Rule | null>(null);
  const [dataContent, setDataContent] = React.useState<string | null | undefined>("");
  const [popupName, setPopupName] = React.useState("");

  const deleteRuleMutation = useDeleteRule();
  const approveRuleMutation = useApproveRule();
  const createCommentMutation = useCreateComment();

  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (topLevelTypesData) {
      setCurrentData(topLevelTypesData);
    }
  }, [topLevelTypesData]);

  function formatDate(dateString: string): string {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return "";
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  function handleDeleteClick(e: React.MouseEvent, item: Rule) {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }

  function handleApprove(e: React.MouseEvent, item: Rule) {
    e.stopPropagation();
    setItemToApprove(item);
    setApproveDialogOpen(true);
  }

  function handleConfirmDelete(authCode?: string) {
    if (!itemToDelete) return;

    deleteRuleMutation.mutate({
      key: itemToDelete.ID,
      authCode: authCode,
    });
  }

  function handleConfirmComment(comm: string, data: Rule) {
    if (!comm) return;

    createCommentMutation.mutate({
      ruleId: data.ID,
      text: comm.trim(),
    });
  }

  function handleConfirmApprove() {
    if (!itemToApprove) return;

    approveRuleMutation.mutate({
      key: itemToApprove?.ID,
      status: itemToApprove?.Approved,
    });
  }

  const ruleTypeOptions = [
    { value: "0", label: "Eligibility Rules" },
    { value: "1", label: "Pricing Rules" },
    { value: "2", label: "Credit Rating Rules" },
    { value: "4", label: "Workflow Entry Conditions" },
    { value: "5", label: "Compliance Rules" },
    { value: "6", label: "Form Rules" },
  ];

  const getRuleTypeLabel = (typeValue: string) => {
    const option = ruleTypeOptions.find((opt) => opt.value === typeValue);
    return option ? option.label : "Unknown";
  };

  React.useEffect(() => {
    if (createCommentMutation.isSuccess === true) {
      setShowPopup(false);
      setReqStatus(createCommentMutation.isSuccess);
      setAlertMessage(`Comment added successfully!`);
      setAlertOpen(true);
      createCommentMutation.reset();
    } else if (createCommentMutation.isError) {
      setShowPopup(false);
      const raw = createCommentMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(createCommentMutation.isSuccess);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      createCommentMutation.reset();
    }
  }, [createCommentMutation]);

  React.useEffect(() => {
    if (deleteRuleMutation.isSuccess === true) {
      setDeleteDialogOpen(false);
      setReqStatus(deleteRuleMutation.isSuccess);
      setAlertMessage(`Rule deleted Successfully!`);
      setItemToDelete(null);
      setAlertOpen(true);
      deleteRuleMutation.reset();
    } else if (deleteRuleMutation.isError) {
      const raw = deleteRuleMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(deleteRuleMutation.isSuccess);
      setDeleteDialogOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      deleteRuleMutation.reset();
    }
  }, [deleteRuleMutation]);

  React.useEffect(() => {
    if (approveRuleMutation.isSuccess === true) {
      setApproveDialogOpen(false);
      setReqStatus(approveRuleMutation.isSuccess);
      setAlertMessage(`${itemToApprove?.Approved === 1 ? "Rule UnApproved Successfully!" : "Rule Approved Successfully!"}`);
      setItemToApprove(null);
      setAlertOpen(true);
      approveRuleMutation.reset();
    } else if (approveRuleMutation.isError) {
      const raw = approveRuleMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(approveRuleMutation.isSuccess);
      setApproveDialogOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      approveRuleMutation.reset();
    }
  }, [approveRuleMutation]);

  return (
    <div>
      <div className="border bg-white border-gray-200 rounded-lg p-2">
        <Table>
          <TableHeader>
            <TableRow>
              <>
                <TableHead>Rule #</TableHead>
                <TableHead>Rule Name</TableHead>
                <TableHead>Rule Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Rule</TableHead>
                <TableHead>Date Created</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Approve Status</TableHead>
                <TableHead>Actions</TableHead>
                <TableHead>Date Modified</TableHead>
                <TableHead>Application ID</TableHead>
              </>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentData?.map((data) => (
              <TableRow key={data.ID}>
                <TableCell className="font-mono">{data.ID}</TableCell>

                <TableCell className="font-mono font-medium">
                  {data.Name}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {getRuleTypeLabel(String(data.Type))}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant={"link"}
                    onClick={() => {
                      setPopupName("Description")
                      setDataHead(data);
                      setDataContent(data.Description);
                      setShowPopup(true);
                    }}
                  >
                    View
                  </Button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant={"link"}
                    onClick={() => {
                      setPopupName("Creation")
                      setDataHead(data);
                      setDataContent(data.Body);
                      setShowPopup(true);
                    }}
                  >
                    View
                  </Button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {formatDate(data.CreatedDate)}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant={"link"}
                    onClick={() => {
                      setPopupName("Comment")
                      setDataHead(data);
                      setDataContent(data.StatusDescription);
                      setShowPopup(true);
                    }}
                  >
                    View
                  </Button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.Approved === 1 ? (
                    <Button variant={"link"}>
                      <Check className="h-4 w-4 mr-1 text-green-800" />
                    </Button>
                  ) : (
                    <Button variant={"link"}>
                      <X className="h-4 w-4 mr-1 text-red-800" />
                    </Button>
                  )}
                </TableCell>

                <TableCell>
                  <div className="flex flex-row items-center space-x-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="min-w-6 h-6 p-0"
                          tooltip="More actions"
                        >
                          <Plus className="h-3 w-3" />
                          <span className="sr-only">More actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="min-w-[180px]"
                      >

                        <DropdownMenuItem
                          onClick={(e) => handleApprove(e, data)}
                          className="flex items-center px-1 cursor-pointer"
                        >
                          {data.Approved === 0 ? (
                            <Check className="h-4 w-4 mr-1 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 mr-1 text-red-600" />
                          )}
                          <span className="text-sm">
                            {data.Approved === 0 ? "Approve" : "Unapprove"}
                          </span>
                        </DropdownMenuItem>

                        <DropdownMenuItem asChild>
                          <CreateRulesDialog
                            isEdit={true}
                            editData={data}
                            trigger={
                              <div className="flex items-center px-1 py-2 cursor-pointer w-full hover:bg-accent">
                                <Edit className="h-4 w-4 mr-3" />
                                <span className="text-sm">
                                  Edit Rule
                                </span>
                              </div>
                            }
                          />
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            const encodedName = encodeURIComponent(data.Name);
                            queryClient.setQueryData(["selectedRule"], data);
                            localStorage.setItem("selectedRule", JSON.stringify(data));
                            setLocation(
                              `/rules/edit/${data.ID}?name=${encodedName}`
                            );
                          }}
                          className="flex items-center px-1 cursor-pointer"
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          <span className="text-sm">Edit Builder</span>
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={(e) => handleDeleteClick(e, data)}
                          className="flex items-center px-1 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 px-1"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          <span className="text-sm">
                            Delete
                          </span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>


                <TableCell className="max-w-xs truncate">
                  {formatDate(data.ModifiedDate)}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.ApplicationID}
                </TableCell>

              </TableRow >
            ))
            }
          </TableBody >
        </Table >

        <div className="mt-2">
          <PaginationControl
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
          />
        </div>
      </div >

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isPending={deleteRuleMutation.isPending}
        itemName={(itemToDelete as Rule)}
        itemType={"Rules"}
        requireAuthCode={itemToDelete?.Approved === 1 ? false : true}
      />

      <AlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        message={alertMessage}
        status={reqStatus}
      />

      <ConfirmationDialog
        open={approveDialogOpen}
        onOpenChange={setApproveDialogOpen}
        onConfirm={handleConfirmApprove}
        isPending={approveRuleMutation.isPending}
        heading={itemToApprove?.Approved === 1 ? "Confirm UnApprove Request" : "Confirm Approve Request"}
        message={`Are you sure you want to ${itemToApprove?.Approved === 1 ? "UnApprove" : "Approve"} this Rule?`}
        confirmLabel={`${itemToApprove?.Approved === 1 ? "UnApprove" : "Approve"}`}
      // cancelLabel="Cancel"
      />

      {
        showPopup && (
          <Popup
            popupName={popupName}
            heading={dataHead}
            content={dataContent}
            onClose={() => setShowPopup(false)}
            onChange={handleConfirmComment}
            isPending={createCommentMutation.isPending}
          />
        )
      }

    </div >
  );
}

function RuleHierarchicalNode({ item, level = 0 }: HierarchicalNodeProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Rule | null>(null);

  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [reqStatus, setReqStatus] = React.useState(false);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [itemToApprove, setItemToApprove] = useState<Rule | null>(null);

  const deleteRuleMutation = useDeleteRule();
  const approveRuleMutation = useApproveRule();

  const [showPopup, setShowPopup] = React.useState(false);
  const [dataHead, setDataHead] = React.useState<Rule | null>(null);
  const [dataContent, setDataContent] = React.useState<string | null | undefined>("");

  const [location, setLocation] = useLocation();

  function handleDeleteClick(e: React.MouseEvent, item: Rule) {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }

  function handleApprove(e: React.MouseEvent, item: Rule) {
    e.stopPropagation();
    setItemToApprove(item);
    setApproveDialogOpen(true);
  }

  function handleConfirmApprove() {
    if (!itemToApprove) return;

    approveRuleMutation.mutate({
      key: itemToApprove?.ID,
      status: itemToApprove?.Approved,
    });
  }

  function handleConfirmDelete(authCode?: string) {
    if (!itemToDelete) return;

    deleteRuleMutation.mutate({
      key: itemToDelete.ID,
      authCode: authCode,
    });
  }

  React.useEffect(() => {
    if (deleteRuleMutation.isSuccess === true) {
      setDeleteDialogOpen(false);
      setReqStatus(deleteRuleMutation.isSuccess);
      setAlertMessage(`Rule deleted Successfully!`);
      setItemToDelete(null);
      setAlertOpen(true);
      deleteRuleMutation.reset();
    } else if (deleteRuleMutation.isError) {
      const raw = deleteRuleMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(deleteRuleMutation.isSuccess);
      setDeleteDialogOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      deleteRuleMutation.reset();
    }
  }, [deleteRuleMutation]);

  React.useEffect(() => {
    if (approveRuleMutation.isSuccess === true) {
      setApproveDialogOpen(false);
      setReqStatus(approveRuleMutation.isSuccess);
      setAlertMessage(`Rule Approved Successfully!`);
      setItemToApprove(null);
      setAlertOpen(true);
      approveRuleMutation.reset();
    } else if (approveRuleMutation.isError) {
      const raw = approveRuleMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(approveRuleMutation.isSuccess);
      setApproveDialogOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      approveRuleMutation.reset();
    }
  }, [approveRuleMutation]);

  return (
    <div className="border bg-white border-gray-200 rounded-lg">
      <div
        className="flex items-center space-x-2 md:space-x-3 p-2 md:p-3 hover:bg-gray-50 cursor-pointer"
        style={{ paddingLeft: `${8 + level * 16}px` }}
      >

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 ml-4">
            <span className="font-medium text-gray-900 truncate text-sm md:text-base">
              {(item as Rule).Name}
            </span>
            <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded flex-shrink-0">
              {(item as Rule).ID}
            </span>
          </div>
          {item.Body && (
            <div className="text-xs md:text-sm text-gray-500 truncate mt-1 ">
              <Button
                variant={"link"}
                onClick={() => {
                  setDataHead(item);
                  setDataContent(item.Body);
                  setShowPopup(true);
                }}
              >
                View Rule
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
          {item.Approved === 1 ? (
            <Button onClick={(e) => handleApprove(e, item)} tooltip="Approve" variant={"link"}>
              <Check size="sm" color="green" />
            </Button>
          ) : (
            <Button onClick={(e) => handleApprove(e, item)} tooltip="unapprove" variant={"link"}>
              <X size="sm" />
            </Button>
          )}

          <CreateRulesDialog
            isEdit={true}
            editData={item}
            trigger={
              <Button
                tooltip="Edit Rule Builder"
                size="sm"
                variant="ghost"
                className="w-7 h-7 md:w-8 md:h-8 p-0"
              >
                <Edit className="h-4 w-4" />
              </Button>
            }
          />

          <Button
            size="sm"
            variant="ghost"
            className="w-7 h-7 md:w-8 md:h-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              const encodedName = encodeURIComponent(item.Name);
              setLocation(`/rules/edit/${item.ID}?name=${encodedName}`);
            }}
            tooltip="Edit Rule Builder"
          >
            <Wrench className="h-4 w-4" />
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => handleDeleteClick(e, item)}
            className="text-red-600"
            tooltip={
              item?.Name ? "Delete this DataType" : "Delete this Attribute"
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>


        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleConfirmDelete}
          isPending={deleteRuleMutation.isPending}
          itemName={(itemToDelete as Rule)}
          itemType={"Rules"}
          requireAuthCode={itemToDelete?.Approved === 1 ? false : true}
        />

        <AlertDialog
          open={alertOpen}
          onOpenChange={setAlertOpen}
          message={alertMessage}
          status={reqStatus}
        />

        <ConfirmationDialog
          open={approveDialogOpen}
          onOpenChange={setApproveDialogOpen}
          onConfirm={handleConfirmApprove}
          isPending={approveRuleMutation.isPending}
          heading={itemToApprove?.Approved === 1 ? "Confirm UnApprove Request" : "Confirm Approve Request"}
          message={`Are you sure you want to ${itemToApprove?.Approved === 1 ? "UnApprove" : "Approve"} this Rule?`}
          confirmLabel={`${itemToApprove?.Approved === 1 ? "UnApprove" : "Approve"}`}
        // cancelLabel="Cancel"
        />

        {
          showPopup && (
            <Popup
              popupName=""
              heading={dataHead}
              content={dataContent}
              onClose={() => setShowPopup(false)}
              onChange={() => console.log("")}
            />
          )
        }
      </div >
    </div >
  );
}

export function RulesManager() {
  const [viewMode, setViewMode] = useState<"table" | "tree">("table");
  const [topLevelTypes, setTopLevelTypes] = useState<Rule[]>([]);

  const [typeInput, setTypeInput] = useState<string>("ALL");
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [filterOpen, setFilterOpen] = useState(false);

  const [filterName, setFilterName] = useState<string>("");
  const [filterTypeData, setFilterTypeData] = useState<FilterType[]>([]);

  // pagination states
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(10);

  const { data: types, isLoading } = useRulesData(
    pageIndex,
    pageSize,
    typeFilter === "ALL" ? undefined : typeFilter
  );

  const { data: filterTypes, isFetching: isFilterFetching } = useGetFilterRule(
    filterName.length >= 3 ? filterName : undefined,
  );

  React.useEffect(() => {
    if (filterTypes && filterTypes.length > 0) {
      setFilterTypeData(filterTypes)
    } else {
      setFilterTypeData([])
    }
  }, [filterTypes])

  React.useEffect(() => {
    setTopLevelTypes(types?.items ?? []);
    setTotalCount(types?.total ?? 0);
  }, [types]);

  // Check for navigation from search
  React.useEffect(() => {
    const selectedItemStr = localStorage.getItem("cm:selectedItem");
    if (selectedItemStr) {
      try {
        const selectedItem = JSON.parse(selectedItemStr);
        console.log("Navigate to item in TreeExplorer:", selectedItem);
        // Clear the selection after handling
        localStorage.removeItem("cm:selectedItem");

        // TODO: Expand tree to show the selected item
        // This could involve expanding parent nodes and highlighting the target
      } catch (error) {
        console.error("Error parsing selected item:", error);
        localStorage.removeItem("cm:selectedItem");
      }
    }
  }, []);

  const handleSearch = () => {
    setTypeFilter(typeInput);
  };

  const handleRefresh = (item: boolean) => {
    setTypeInput("ALL");
    setTypeFilter("ALL");
    if (item) setFilterOpen(false);
  };

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              Rules Manager
            </h2>
            <p className="text-gray-600 mt-1 text-sm md:text-base">
              Manage Rules
            </p>
          </div>

          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <Button
                size="sm"
                variant={viewMode === "table" ? "default" : "ghost"}
                onClick={() => setViewMode("table")}
                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
              >
                <List className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                <span className="hidden sm:inline">Table</span>
                <span className="sm:hidden">Table</span>
              </Button>
              <Button
                size="sm"
                variant={viewMode === "tree" ? "default" : "ghost"}
                onClick={() => setViewMode("tree")}
                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
              >
                <TreePine className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                <span className="hidden sm:inline">Tree</span>
                <span className="sm:hidden">Tree</span>
              </Button>
            </div>
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={() => setFilterOpen(true)}
            >
              Filter Rules
            </Button>
            <CreateRulesDialog
              trigger={
                <Button
                  className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                  size="sm"
                >
                  <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                  Add Rules
                </Button>
              }
            />
          </div>
        </div>
      </div>
      <div>
        {filterOpen &&
          <Card className="mb-4 md:mb-6">
            <CardContent className="pt-0">
              <div className="w-[450px]">
                <FilterTypeSearch
                  filterTypeData={filterTypeData}
                  isLoading={isFilterFetching}
                  value={typeInput}
                  onChange={setTypeInput}
                  onSearch={(query) => setFilterName(query)}
                />
              </div>


              <div className="mt-4 flex justify-center space-x-2">
                <Button onClick={handleSearch}>Search</Button>
                <Button variant="outline" onClick={() => handleRefresh(false)}>
                  Refresh
                </Button>
                <Button variant="outline" onClick={() => handleRefresh(true)}>
                  Close
                </Button>
              </div>

            </CardContent>
          </Card>
        }
      </div>

      {!isLoading ? (
        viewMode === "table" ? (
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="space-y-2">
                {topLevelTypes && topLevelTypes.length > 0 ? (
                  <>
                    <TableRule
                      topLevelTypesData={topLevelTypes}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      totalCount={totalCount}
                      onPageChange={setPageIndex}
                    />
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No Rules found. Create to get started.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="space-y-2">
                {topLevelTypes && topLevelTypes.length > 0 ? (
                  topLevelTypes.map((type) => (
                    <RuleHierarchicalNode key={type.ID} item={type} />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No Rules found! Create to get started.</p>
                  </div>
                )}
                <PaginationControl
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setPageIndex}
                />
              </div>
            </CardContent>
          </Card>
        )
      ) : (
        <div className="p-6">Loading...</div>
      )}
    </div>
  );
}
